<template>
  <div :class="$style.container">
    <component
      :is="monthOfYear"
      v-m-b="isMobile ? 10 : 4"
      :month="month"
      :is-disabled-prev="isDisabledPrevMonth"
      :is-disabled-next="isDisabledNextMonth"
      @prev-month="$emit('prev-month')"
      @next-month="$emit('next-month')"
    ></component>

    <days-of-week />

    <div :class="$style.daysContainer">
      <div
        v-for="(dayIndex, index) in days"
        :key="index"
        :class="[
          $style.dayBlock,
          isDisabled(dayIndex) && $style.disabled,
          isLeft(dayIndex) && $style.leftBorder,
          isRight(dayIndex) && $style.rightBorder,
        ]"
        @mouseenter="onMouseHoverDay(dayIndex)"
      >
        <div
          v-if="isCurrentMonth(dayIndex)"
          :class="[
            $style.day,
            isDarkHighlight(dayIndex) && $style.darkHighlight,
            isHighlighted(dayIndex) && $style.highlight,
          ]"
          @click="onClickDay(dayIndex)"
        >
          {{ dayIndex + 1 }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  boundsNumber,
  toggleRange,
  transformDateValue
} from 'lib/utils/dateRange';

import { isMoment, getMomentConstructor } from 'lib/utils/date';

const moment = getMomentConstructor();

import DaysOfWeek from 'molecules/datetime/DaysOfWeek';
import MonthOfYear from 'molecules/datetime/desktop/MonthOfYear';

export default {
  name: 'DatepickerPalette',
  components: {
    DaysOfWeek,
    MonthOfYear,
  },
  props: {
    value: {
      type: Object,
      default: () => moment(),
    },
    // нужна для внешнего управления наведением
    hoverDay: Object,
    month: Object,
    // 0 - первый день понедельник, 1 - воскресенье
    firstDayOffset: {
      type: Number,
      default: 0,
    },
    min: Object,
    max: Object,
    range: Boolean,
    isMobile: Boolean,
  },
  mounted() {
    this.currentMonth = transformDateValue(this.month || this.value || moment(), false)
      .startOf('month');

    this.updateValue(this.value);
  },
  data() {
    return {
      date: null,
      dateRange: { from: null, to: null },
      currentMonth: moment().startOf('month'),
      hoveredDayIndex: null,
    };
  },
  computed: {
    monthOfYear() {
      if (this.isMobile) {
        return () => import('molecules/datetime/mobile/MonthOfYear');
      }

      return () => import('molecules/datetime/desktop/MonthOfYear');
    },
    days() {
      let res = Array.from({ length: 6 * 7 }).map((_, index) => index - this.weekOffset - 7);

      if (!res.slice(0, 7).some((dayIndex) => this.isCurrentMonth(dayIndex))) {
        res = res.slice(7);
      }

      if (!res.slice(-7).some((dayIndex) => this.isCurrentMonth(dayIndex))) {
        res = res.slice(0, -7);
      }

      return res;
    },
    firstDayOfMonth() {
      return this.currentMonth.clone().startOf('month');
    },
    daysInMonth() {
      return this.currentMonth.daysInMonth();
    },
    weekOffset() {
      return this.firstDayOfMonth.day() - 1 + this.firstDayOffset;
    },
    currentDayIndex() {
      const dayIndex = this.date ? Number(this.date.diff(this.currentMonth, 'days')) : -1;

      return boundsNumber(dayIndex, [this.days[0] - 1, this.days[this.days.length - 1] + 1]);
    },
    rangeFromIndex() {
      if (this.range) {
        const fromIndex = this.dateRange?.from
          ? this.dateRange.from.diff(this.currentMonth, 'days') : null;

        return boundsNumber(fromIndex, [this.days[0] - 1, this.days[this.days.length - 1] + 1]);
      }

      return null;
    },
    rangeToIndex() {
      if (this.range) {
        const toIndex = this.dateRange?.to
          ? this.dateRange.to.diff(this.currentMonth, 'days') : null;

        return boundsNumber(toIndex, [this.days[0] - 1, this.days[this.days.length - 1] + 1]);
      }

      return null;
    },
    rangeSelectedIndex() {
      let range = [];

      if (this.rangeFromIndex !== null && this.rangeToIndex !== null) {
        range = [this.rangeFromIndex, this.rangeToIndex];
      } else {
        range = [this.getRangeSelectedOneIndex(), this.hoveredDayIndex];
      }

      return range.sort((a, b) => a - b).filter(index => index !== null);
    },
    minIndex() {
      return this.min && isMoment(this.min)
        ? this.min.diff(this.currentMonth, 'days') : -Infinity;
    },
    maxIndex() {
      return this.max && isMoment(this.max)
        ? this.max.diff(this.currentMonth, 'days') : Infinity;
    },
    minMaxRange() {
      return [Math.min(this.minIndex, this.maxIndex), Math.max(this.minIndex, this.maxIndex)];
    },
    isDisabledPrevMonth() {
      return this.currentMonth.isSame(this.min);
    },
    isDisabledNextMonth() {
      return this.currentMonth.isSame(moment(this.max).startOf('month'));
    },
  },
  watch: {
    value(newValue) {
      this.updateValue(newValue);
    },
    hoverDay(hoveredDay) {
      if (!hoveredDay) {
        this.hoveredDayIndex = null;
        return;
      }

      // bounds нужны для того, чтобы не вызывался персчёт данных по всему календарю,
      // если ховерят на значение за пределами календаря
      // другие boundsNumber отвечают примерно за то же
      this.hoveredDayIndex = boundsNumber(
        hoveredDay.diff(this.currentMonth, 'days'),
        [this.days[0] - 1, this.days[this.days.length - 1] + 1]
      );
    },
    month(newMonth) {
      this.currentMonth = transformDateValue(newMonth || moment(), false)
        .startOf('month');
    },
  },
  methods: {
    updateValue(newValue) {
      if (this.range) {
        this.dateRange = transformDateValue(newValue, true);
      } else {
        this.date = transformDateValue(newValue, false);
      }
    },
    onClickDay(dayIndex) {
      if (this.isDisabled(dayIndex)) {
        return;
      }

      const clickedDay = this.currentMonth.clone().add(dayIndex, 'days');

      if (this.range) {
        this.dateRange = toggleRange(this.dateRange, clickedDay);
        this.$emit('input', this.dateRange);
      } else {
        this.date = clickedDay;
        this.$emit('input', clickedDay);
      }
    },
    onMouseHoverDay(dayIndex) {
      if (this.isDisabled(dayIndex)) {
        return;
      }

      if (this.isCurrentMonth(dayIndex)) {
        this.hoveredDayIndex = dayIndex;
        this.$emit('hover:day', this.currentMonth.clone().add(this.hoveredDayIndex, 'days'));
      }
    },
    isCurrentMonth(dayIndex) {
      return dayIndex >= 0 && dayIndex < this.daysInMonth;
    },
    isHighlighted(dayIndex) {
      if (this.range) {
        return this.rangeFromIndex === dayIndex || this.rangeToIndex === dayIndex;
      }

      return this.currentDayIndex === dayIndex;
    },
    // eslint-disable-next-line complexity
    isDarkHighlight(dayIndex) {
      if (this.range) {
        const rangeIndices = this.rangeSelectedIndex;

        return rangeIndices.length === 2 && dayIndex >= rangeIndices[0] && dayIndex <= rangeIndices[1];
      }

      return false;
    },
    isLeft(dayIndex) {
      if (!this.range) {
        return true;
      }

      return dayIndex === this.rangeSelectedIndex[0];
    },
    isRight(dayIndex) {
      if (!this.range) {
        return true;
      }

      return dayIndex === this.rangeSelectedIndex[1];
    },
    isDisabled(dayIndex) {
      return (dayIndex < this.minMaxRange[0]) || dayIndex > this.minMaxRange[1];
    },
    getRangeSelectedOneIndex() {
      //выбирает одно значение из выбранного интервала, по идее будет только в from, но лучше перестраховаться
      if (this.rangeFromIndex !== null) {
        return this.rangeFromIndex;
      }

      if (this.rangeToIndex !== null) {
        return this.rangeToIndex;
      }

      return null;
    },
  },
};
</script>

<style lang="sass" module>
  .container
    --row-height: 36px
    --row-border-weight: 2px

    display: flex
    flex-direction: column
    font-size: 14px
    color: #fff
    background: var(--daypicker-bg-color)
    user-select: none
    scroll-snap-align: start

  .daysContainer
    display: grid
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr
    min-width: 304px
    grid-template-rows: calc(var(--row-height) + calc(2 * var(--row-border-weight)))

  .day
    height: var(--row-height)
    text-align: center
    line-height: var(--row-height)

  .dayBlock
    cursor: pointer
    border-top: var(--row-border-weight) solid var(--daypicker-bg-color)
    border-bottom: var(--row-border-weight) solid var(--daypicker-bg-color)

  .day
    &:not(.highlight)
      &:not(.darkHighlight)
        &:hover
          background: #32571D
          border-radius: 5px

  .darkHighlight
    background: #32571D

    &:hover
      opacity: 0.7

  .highlight
    background: #7CB342

  .leftBorder
    border-top-left-radius: 5px
    border-bottom-left-radius: 5px

    .highlight
      border-top-left-radius: 5px
      border-bottom-left-radius: 5px

  .rightBorder
    border-top-right-radius: 5px
    border-bottom-right-radius: 5px

    .highlight
      border-top-right-radius: 5px
      border-bottom-right-radius: 5px

  .disabled
    opacity: 0.6
    cursor: not-allowed
    pointer-events: none
</style>
